import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormLabel,
  InputAdornment,
  Switch,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DescriptionIcon from "@mui/icons-material/Description";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkIcon from "@mui/icons-material/Link";
import HomeIcon from "@mui/icons-material/Home";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  Edit,
  Save,
  Cancel,
  Delete,
  Image,
  Call as CallIcon,
  Share as ShareIcon,
  MoreHoriz as MoreHorizIcon,
  Place as PlaceIcon,
  CloudUpload as CloudUploadIcon,
  Label as LabelIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import Axios from "../../Common/api/axiosConfig";
import { uploadLink } from "../../utils/constants";
import ClearIcon from "@mui/icons-material/Clear";

import PaletteIcon from "@mui/icons-material/Palette";
import CampaignIcon from "@mui/icons-material/Campaign";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { STORE_THEMES } from "../store/themes/storeThemes";
import CategoryManager from "./CategoryManager";
import CountryCodePhoneInput from "../../Components/Common/CountryCodePhoneInput";
import { onActiveStoreChange } from "../../utils/storeHelper";
import {
  CategoryItem,
  DEFAULT_CATEGORIES,
  getStoreCategories,
  saveCategoriesLocally,
} from "../../utils/categoryHelper";

interface ThemeSettings {
  theme_id: string;
  accent_color: string;
  font_pairing: string;
  card_style: string;
}

interface AnnouncementSettings {
  show_announcement: boolean;
  top_tagline: string;
  announcement_text: string;
  location_badge: string;
}

interface HeroSettings {
  headline: string;
  headline_italic: string;
  description: string;
  badge_text: string;
  quote_script: string;
  tags_string: string;
  primary_cta_label: string;
  whatsapp_cta_label: string;
  card_title: string;
  card_subtitle: string;
  card_delivery_text: string;
  show_dotted_pattern: boolean;
  banner_image?: string;
}

interface TrustBadgeSettings {
  show_badges: boolean;
  badge1_title: string;
  badge1_subtitle: string;
  badge2_title: string;
  badge2_subtitle: string;
  badge3_title: string;
  badge3_subtitle: string;
  badge4_title: string;
  badge4_subtitle: string;
}

interface BrandNoteSettings {
  show_banner: boolean;
  title: string;
  subtitle: string;
  script_text: string;
}

interface StoreHeaderSettings {
  logo?: string;
  favicon?: string;
  store_name: string;
  description: string;
  tagline: string;
  phone_number: string;
  location: string;
  show_logo: boolean;
  show_store_name: boolean;
  show_description: boolean;
  show_location: boolean;
  show_call_button: boolean;
  show_share_button: boolean;
  show_more_button: boolean;
  call_button_label: string;
  share_button_label: string;
  more_button_label: string;
}

interface Preferences {
  storeVisibility: boolean;
  whatsappNotifications: boolean;
  autoReply: boolean;
}

type PreferenceKey = keyof Preferences;

interface FormData {
  businessName: string;
  description: string;
  whatsappNumber: string;
  storeUrl: string;
  address: string;
  operatingHours: string;
  deliveryInfo: string;
  images: File[];
}

interface FormErrors {
  businessName?: string;
  description?: string;
  whatsappNumber?: string;
  storeUrl?: string;
  address?: string;
  operatingHours?: string;
  deliveryInfo?: string;
  images?: string;
}

const StoreSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [storeId, setStoreID] = useState("");
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const [formData, setFormData] = useState<FormData>({
    businessName: "",
    description: "",
    whatsappNumber: "",
    storeUrl: "",
    address: "",
    operatingHours: "",
    deliveryInfo: "",
    images: [],
  });

  const [preferences, setPreferences] = useState<Preferences>({
    storeVisibility: true,
    whatsappNotifications: true,
    autoReply: false,
  });

  const [headerSettings, setHeaderSettings] = useState<StoreHeaderSettings>({
    logo: "",
    store_name: "",
    description: "",
    tagline: "",
    phone_number: "",
    location: "",
    show_logo: true,
    show_store_name: true,
    show_description: true,
    show_location: true,
    show_call_button: true,
    show_share_button: true,
    show_more_button: true,
    call_button_label: "Call",
    share_button_label: "Share",
    more_button_label: "More",
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [removeLogo, setRemoveLogo] = useState<boolean>(false);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string>("");
  const [removeFavicon, setRemoveFavicon] = useState<boolean>(false);

  const [themeSettings, setThemeSettings] = useState<ThemeSettings>({
    theme_id: "luxury_warm",
    accent_color: "#C5896E",
    font_pairing: "serif_luxury",
    card_style: "elevated_rounded",
  });

  const [announcementSettings, setAnnouncementSettings] = useState<AnnouncementSettings>({
    show_announcement: true,
    top_tagline: "Elegance Meets Care — Premium Self-Care Essentials",
    announcement_text:
      "✨ Free Delivery All Over India • 🛡️ 100% Quality Checked Products • 🔄 Easy Returns & Exchange",
    location_badge: "Thane, India",
  });

  const [heroSettings, setHeroSettings] = useState<HeroSettings>({
    headline: "Your Everyday",
    headline_italic: "Self-Care Companion",
    description:
      "Elevate your daily ritual with salon-grade hair stylers and rejuvenating smart massagers, designed for timeless grace and wellness.",
    badge_text: "Elegance Meets Care",
    quote_script: "Care Looks Good On You ♡",
    tags_string: "🪷 Beauty, ♡ Wellness, 🍃 Better You",
    primary_cta_label: "Shop Best Sellers",
    whatsapp_cta_label: "Order via WhatsApp",
    card_title: "Beauty & Personal Therapy",
    card_subtitle: "Featured Collection",
    card_delivery_text: "Direct Delivery in Thane & All-India",
    show_dotted_pattern: true,
    banner_image: "",
  });
  const [heroBannerFile, setHeroBannerFile] = useState<File | null>(null);
  const [heroBannerPreview, setHeroBannerPreview] = useState<string>("");
  const [removeHeroBanner, setRemoveHeroBanner] = useState<boolean>(false);

  const [trustBadgeSettings, setTrustBadgeSettings] = useState<TrustBadgeSettings>({
    show_badges: true,
    badge1_title: "Free Delivery",
    badge1_subtitle: "All Over India",
    badge2_title: "Quality Checked",
    badge2_subtitle: "100% Tested Products",
    badge3_title: "Quick Support",
    badge3_subtitle: "Instant on WhatsApp",
    badge4_title: "Easy Returns",
    badge4_subtitle: "Hassle-Free Exchange",
  });

  const [brandNoteSettings, setBrandNoteSettings] = useState<BrandNoteSettings>({
    show_banner: true,
    title: "🤎 Thank you for supporting our small business!",
    subtitle: "RUDRIX | Elegance Meets Care",
    script_text: "Self Care Brighter You ♡",
  });

  // Category Settings state
  const [categories, setCategories] = useState<CategoryItem[]>(DEFAULT_CATEGORIES);
  const [showCategorySection, setShowCategorySection] = useState<boolean>(true);
  const [categorySaving, setCategorySaving] = useState<boolean>(false);

  const handleSaveCategories = async () => {
    if (!storeId) return;
    try {
      setCategorySaving(true);
      saveCategoriesLocally(categories, storeId);

      const payload = new FormData();
      payload.append("categories", JSON.stringify(categories));
      payload.append("show_category_section", String(showCategorySection));

      const updatedHeader = {
        ...headerSettings,
        categories: categories,
        show_category_section: showCategorySection,
      };
      payload.append("header", JSON.stringify(updatedHeader));

      await Axios.patch(`/store/${storeId}`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess("Categories updated successfully!");
      setOpenSnackbar(true);
    } catch (err: any) {
      console.error("Error saving categories:", err);
      setSuccess("Categories saved locally!");
      setOpenSnackbar(true);
    } finally {
      setCategorySaving(false);
    }
  };

  // Validation functions
  const validateField = (name: keyof FormData, value: any): string => {
    switch (name) {
      case "images":
        if (Array.isArray(value)) {
          for (const file of value) {
            if (
              !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
                file.type
              )
            ) {
              return "Only JPEG, PNG, and WebP images are allowed";
            }
            if (file.size > 5 * 1024 * 1024) {
              return "Each image must be less than 5MB";
            }
          }
        }
        return "";

      case "businessName":
        if (!value) return "Business name is required";
        if (value.length < 2)
          return "Business name must be at least 2 characters";
        if (value.length > 100)
          return "Business name must be less than 100 characters";
        return "";

      case "description":
        if (!value) return "Description is required";
        if (value.length < 10)
          return "Description must be at least 10 characters";
        if (value.length > 500)
          return "Description must be less than 500 characters";
        return "";

      case "whatsappNumber":
        const strVal =
          value !== undefined && value !== null ? String(value).trim() : "";
        if (!strVal) return "WhatsApp number is required";
        const digitsOnly = strVal.replace(/[^0-9]/g, "");
        if (digitsOnly.length < 10)
          return "WhatsApp number must be at least 10 digits including country code";
        if (digitsOnly.length > 16)
          return "WhatsApp number must be less than 16 digits";
        return "";

      case "storeUrl":
        if (!value) return "Store URL is required";
        const cleanStoreUrl = String(value).trim().toLowerCase();
        if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*$/i.test(cleanStoreUrl))
          return "Store URL can only contain lowercase letters, numbers, hyphens, and dots (for domains)";
        if (cleanStoreUrl.length < 2) return "Store URL must be at least 2 characters";
        if (cleanStoreUrl.length > 80)
          return "Store URL must be less than 80 characters";
        return "";

      case "address":
        if (value && value.length > 200)
          return "Address must be less than 200 characters";
        return "";

      case "operatingHours":
        if (value && value.length > 100)
          return "Operating hours must be less than 100 characters";
        return "";

      case "deliveryInfo":
        if (value && value.length > 300)
          return "Delivery information must be less than 300 characters";
        return "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    (Object.keys(formData) as Array<keyof FormData>).forEach((key) => {
      if (key !== "images") {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key] = error;
          isValid = false;
        }
      }
    });

    // Validate images separately
    if (formData.images.length > 0) {
      const imageError = validateField("images", formData.images);
      if (imageError) {
        newErrors.images = imageError;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (touched[name]) {
      const error = validateField(name as keyof FormData, value);
      setErrors((prev) => ({ ...prev, [name]: error || undefined }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof FormData, value);
    setErrors((prev) => ({ ...prev, [name]: error || undefined }));
  };

  const handleMultipleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate files
    const error = validateField("images", files);
    if (error) {
      setErrors((prev) => ({ ...prev, images: error }));
      return;
    }

    // Add new images to existing ones
    const newImages = [...formData.images, ...files];
    setFormData((prev) => ({ ...prev, images: newImages }));
    setErrors((prev) => ({ ...prev, images: undefined }));

    // Create previews for new images
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = "";
  };

  const handleRemoveImage = (index: number) => {
    // Check if it's a newly uploaded image or existing preview
    const totalExistingPreviews = imagePreviews.length - formData.images.length;

    if (index < totalExistingPreviews) {
      // It's an existing image preview - track it for removal
      const imageToRemove = imagePreviews[index];

      // Extract the original image path (remove uploadLink if present)
      let originalImagePath = imageToRemove;
      if (imageToRemove.includes(uploadLink)) {
        originalImagePath = imageToRemove.replace(uploadLink, "");
      }

      // Add to removed images array
      setRemovedImages((prev) => [...prev, originalImagePath]);

      // Remove from previews
      const newPreviews = [...imagePreviews];
      newPreviews.splice(index, 1);
      setImagePreviews(newPreviews);
    } else {
      // It's a newly uploaded image - remove from both formData and previews
      const newImages = [...formData.images];
      const newPreviews = [...imagePreviews];

      const fileIndex = index - totalExistingPreviews;
      newImages.splice(fileIndex, 1);
      newPreviews.splice(index, 1);

      setFormData((prev) => ({ ...prev, images: newImages }));
      setImagePreviews(newPreviews);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml"].includes(
        file.type
      )
    ) {
      setError("Please upload a valid logo image (JPG, PNG, WEBP, or SVG)");
      setOpenSnackbar(true);
      return;
    }

    setLogoFile(file);
    setRemoveLogo(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    setRemoveLogo(true);
    setHeaderSettings((prev) => ({ ...prev, logo: "" }));
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml", "image/x-icon", "image/vnd.microsoft.icon"].includes(
        file.type
      ) && !file.name.endsWith(".ico")
    ) {
      setError("Please upload a valid icon or image (ICO, PNG, SVG, JPG, WEBP)");
      setOpenSnackbar(true);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError("Favicon image must be less than 2MB");
      setOpenSnackbar(true);
      return;
    }

    setFaviconFile(file);
    setRemoveFavicon(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setFaviconPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveFavicon = () => {
    setFaviconFile(null);
    setFaviconPreview("");
    setRemoveFavicon(true);
    setHeaderSettings((prev) => ({ ...prev, favicon: "" }));
  };

  const handleHeroBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
        file.type
      )
    ) {
      setError("Please upload a valid image (JPG, PNG, or WEBP)");
      setOpenSnackbar(true);
      return;
    }

    setHeroBannerFile(file);
    setRemoveHeroBanner(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setHeroBannerPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveHeroBanner = () => {
    setHeroBannerFile(null);
    setHeroBannerPreview("");
    setRemoveHeroBanner(true);
    setHeroSettings((prev) => ({ ...prev, banner_image: "" }));
  };

  const handleHeaderInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setHeaderSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeaderToggleChange = (name: keyof StoreHeaderSettings) => {
    setHeaderSettings((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix all validation errors");
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!storeId) {
        setError("Store ID not found. Please refresh the page.");
        setOpenSnackbar(true);
        setLoading(false);
        return;
      }

      const payload = new FormData();
      payload.append("name", String(formData.businessName || "").trim());
      payload.append("description", String(formData.description || "").trim());
      payload.append("number", String(formData.whatsappNumber || "").trim());
      payload.append("url", String(formData.storeUrl || "").trim());
      payload.append("address", String(formData.address || "").trim());
      payload.append("operating_hours", String(formData.operatingHours || "").trim());
      payload.append("delivery", String(formData.deliveryInfo || "").trim());

      // Add multiple images
      formData.images.forEach((image) => {
        payload.append("image", image);
      });

      // Add removed images
      if (removedImages.length > 0) {
        payload.append("removedImages", JSON.stringify(removedImages));
      }

      // Add Store Profile Header settings
      const headerWithCats = {
        ...headerSettings,
        favicon: faviconPreview,
        categories: categories,
        show_category_section: showCategorySection,
      };
      payload.append("header", JSON.stringify(headerWithCats));
      if (logoFile) {
        payload.append("logo", logoFile);
      }
      if (removeLogo) {
        payload.append("removeLogo", "true");
      }
      if (faviconFile) {
        payload.append("favicon", faviconFile);
      }
      if (removeFavicon) {
        payload.append("removeFavicon", "true");
        try {
          localStorage.removeItem(`rudrix_store_favicon_${storeId}`);
        } catch (_) {}
      }
      if (heroBannerFile) {
        payload.append("hero_banner", heroBannerFile);
      }
      if (removeHeroBanner) {
        payload.append("removeHeroBanner", "true");
      }

      // Add Theme and Sections
      payload.append("theme", JSON.stringify(themeSettings));
      payload.append("announcement", JSON.stringify(announcementSettings));
      payload.append(
        "hero",
        JSON.stringify({
          ...heroSettings,
          tags: heroSettings.tags_string
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        })
      );
      payload.append("trust_badges", JSON.stringify(trustBadgeSettings));
      payload.append("brand_note", JSON.stringify(brandNoteSettings));
      payload.append("categories", JSON.stringify(categories));
      payload.append("show_category_section", String(showCategorySection));
      saveCategoriesLocally(categories, storeId);

      // API call to update store
      const response = await Axios.patch(`/store/${storeId}`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const updatedStore = response.data?.store || response.data?.data;
      if (updatedStore?.header) {
        const h = updatedStore.header;
        setHeaderSettings({
          logo: h.logo || "",
          store_name:
            h.store_name !== undefined && h.store_name !== ""
              ? h.store_name
              : updatedStore.name || "",
          description:
            h.description !== undefined && h.description !== ""
              ? h.description
              : updatedStore.description || "",
          tagline: h.tagline || "",
          phone_number:
            h.phone_number !== undefined && h.phone_number !== ""
              ? h.phone_number
              : updatedStore.number
              ? String(updatedStore.number)
              : "",
          location:
            h.location !== undefined && h.location !== ""
              ? h.location
              : updatedStore.address || "",
          show_logo: h.show_logo !== undefined ? Boolean(h.show_logo) : true,
          show_store_name:
            h.show_store_name !== undefined ? Boolean(h.show_store_name) : true,
          show_description:
            h.show_description !== undefined
              ? Boolean(h.show_description)
              : true,
          show_location:
            h.show_location !== undefined ? Boolean(h.show_location) : true,
          show_call_button:
            h.show_call_button !== undefined
              ? Boolean(h.show_call_button)
              : true,
          show_share_button:
            h.show_share_button !== undefined
              ? Boolean(h.show_share_button)
              : true,
          show_more_button:
            h.show_more_button !== undefined
              ? Boolean(h.show_more_button)
              : true,
          call_button_label: h.call_button_label || "Call",
          share_button_label: h.share_button_label || "Share",
          more_button_label: h.more_button_label || "More",
        });

        if (h.logo) {
          const clean = h.logo.startsWith("/") ? h.logo.slice(1) : h.logo;
          const fullLogoUrl =
            h.logo.startsWith("http") || h.logo.startsWith("data:")
              ? h.logo
              : `${uploadLink}${clean}`;
          setLogoPreview(fullLogoUrl);
        } else {
          setLogoPreview("");
        }
      }

      setSuccess("Store settings updated successfully!");
      setOpenSnackbar(true);
      setIsEditing(false);
      setRemovedImages([]);
      setLogoFile(null);
      setRemoveLogo(false);
      setHeroBannerFile(null);
      setRemoveHeroBanner(false);
      setFaviconFile(null);
      setRemoveFavicon(false);
      if (faviconPreview) {
        try {
          localStorage.setItem(`rudrix_store_favicon_${storeId}`, faviconPreview);
        } catch (_) {}
      }

      // Refresh store data after successful update
      fetchStoreData();
    } catch (error: any) {
      console.error("Store update error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update store settings. Please try again.";
      setError(errorMessage);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreData = async () => {
    try {
      setFetchLoading(true);
      setError("");

      const userDataString = localStorage.getItem("userData");
      if (!userDataString) {
        setError("User data not found. Please login again.");
        setOpenSnackbar(true);
        setFetchLoading(false);
        return;
      }

      const userData = JSON.parse(userDataString);
      const userId = userData._id || userData.id;

      const response = await Axios.get(`/store/${userId}`);
      const storeData = response.data.store;
      setStoreID(storeData._id);

      setFormData({
        businessName: storeData.name || "",
        description: storeData.description || "",
        whatsappNumber:
          storeData.number !== undefined && storeData.number !== null
            ? String(storeData.number)
            : "",
        storeUrl: storeData.url || "",
        address: storeData.address || "",
        operatingHours: storeData.operating_hours || "",
        deliveryInfo: storeData.delivery || "",
        images: [], // Keep empty for new uploads
      });

      // Handle Store Profile Header data
      const header = storeData.header || {};
      setHeaderSettings({
        logo: header.logo || "",
        store_name:
          header.store_name !== undefined && header.store_name !== ""
            ? header.store_name
            : storeData.name || "",
        description:
          header.description !== undefined && header.description !== ""
            ? header.description
            : storeData.description || "",
        tagline: header.tagline || "",
        phone_number:
          header.phone_number !== undefined && header.phone_number !== ""
            ? header.phone_number
            : storeData.number
            ? String(storeData.number)
            : "",
        location:
          header.location !== undefined && header.location !== ""
            ? header.location
            : storeData.address || "",
        show_logo:
          header.show_logo !== undefined ? Boolean(header.show_logo) : true,
        show_store_name:
          header.show_store_name !== undefined
            ? Boolean(header.show_store_name)
            : true,
        show_description:
          header.show_description !== undefined
            ? Boolean(header.show_description)
            : true,
        show_location:
          header.show_location !== undefined
            ? Boolean(header.show_location)
            : true,
        show_call_button:
          header.show_call_button !== undefined
            ? Boolean(header.show_call_button)
            : true,
        show_share_button:
          header.show_share_button !== undefined
            ? Boolean(header.show_share_button)
            : true,
        show_more_button:
          header.show_more_button !== undefined
            ? Boolean(header.show_more_button)
            : true,
        call_button_label: header.call_button_label || "Call",
        share_button_label: header.share_button_label || "Share",
        more_button_label: header.more_button_label || "More",
      });

      if (header.logo) {
        if (header.logo.startsWith("http") || header.logo.startsWith("data:")) {
          setLogoPreview(header.logo);
        } else {
          const clean = header.logo.startsWith("/")
            ? header.logo.slice(1)
            : header.logo;
          setLogoPreview(`${uploadLink}${clean}`);
        }
      } else {
        setLogoPreview("");
      }
      setLogoFile(null);
      setRemoveLogo(false);

      // Handle favicon
      const rawFav =
        header.favicon ||
        storeData.favicon ||
        (storeId ? localStorage.getItem(`rudrix_store_favicon_${storeId}`) : "") ||
        "";
      if (rawFav) {
        if (rawFav.startsWith("http") || rawFav.startsWith("data:")) {
          setFaviconPreview(rawFav);
        } else {
          const clean = rawFav.startsWith("/") ? rawFav.slice(1) : rawFav;
          setFaviconPreview(`${uploadLink}${clean}`);
        }
      } else {
        setFaviconPreview("");
      }
      setFaviconFile(null);
      setRemoveFavicon(false);

      // Handle existing images - same logic as productPopup
      if (storeData.images && storeData.images.length > 0) {
        const existingImagePreviews = storeData.images.map((image: string) => {
          // Check if image already has full URL or needs to be constructed
          if (image.startsWith("http")) {
            return image;
          } else {
            return `${uploadLink}${image}`;
          }
        });
        setImagePreviews(existingImagePreviews);
      } else {
        setImagePreviews([]);
      }

      // Reset removed images when data is fetched
      setRemovedImages([]);

      // Theme settings
      if (storeData.theme) {
        setThemeSettings({
          theme_id: storeData.theme.theme_id || "luxury_warm",
          accent_color: storeData.theme.accent_color || "#C5896E",
          font_pairing: storeData.theme.font_pairing || "serif_luxury",
          card_style: storeData.theme.card_style || "elevated_rounded",
        });
      }

      // Announcement settings
      if (storeData.announcement) {
        setAnnouncementSettings({
          show_announcement:
            storeData.announcement.show_announcement !== undefined
              ? Boolean(storeData.announcement.show_announcement)
              : true,
          top_tagline:
            storeData.announcement.top_tagline ||
            "Elegance Meets Care — Premium Self-Care Essentials",
          announcement_text:
            storeData.announcement.announcement_text ||
            "✨ Free Delivery All Over India • 🛡️ 100% Quality Checked Products • 🔄 Easy Returns & Exchange",
          location_badge:
            storeData.announcement.location_badge ||
            storeData.address ||
            "Thane, India",
        });
      }

      // Hero settings
      if (storeData.hero) {
        const hr = storeData.hero;
        if (hr.banner_image) {
          const clean = hr.banner_image.replace(/^\//, "");
          const bannerUrl =
            hr.banner_image.startsWith("http") || hr.banner_image.startsWith("data:")
              ? hr.banner_image
              : `${uploadLink}${clean}`;
          setHeroBannerPreview(bannerUrl);
        } else {
          setHeroBannerPreview("");
        }
        setHeroSettings({
          headline: hr.headline || "Your Everyday",
          headline_italic: hr.headline_italic || "Self-Care Companion",
          description: hr.description || storeData.description || "",
          badge_text: hr.badge_text || "Elegance Meets Care",
          quote_script: hr.quote_script || "Care Looks Good On You ♡",
          tags_string: Array.isArray(hr.tags)
            ? hr.tags.join(", ")
            : "🪷 Beauty, ♡ Wellness, 🍃 Better You",
          primary_cta_label: hr.primary_cta_label || "Shop Best Sellers",
          whatsapp_cta_label: hr.whatsapp_cta_label || "Order via WhatsApp",
          card_title: hr.card_title || "Beauty & Personal Therapy",
          card_subtitle: hr.card_subtitle || "Featured Collection",
          card_delivery_text:
            hr.card_delivery_text || "Direct Delivery in Thane & All-India",
          show_dotted_pattern:
            hr.show_dotted_pattern !== undefined
              ? Boolean(hr.show_dotted_pattern)
              : true,
          banner_image: hr.banner_image || "",
        });
      }

      // Trust badges
      if (storeData.trust_badges) {
        const tb = storeData.trust_badges;
        setTrustBadgeSettings({
          show_badges:
            tb.show_badges !== undefined ? Boolean(tb.show_badges) : true,
          badge1_title: tb.badge1_title || "Free Delivery",
          badge1_subtitle: tb.badge1_subtitle || "All Over India",
          badge2_title: tb.badge2_title || "Quality Checked",
          badge2_subtitle: tb.badge2_subtitle || "100% Tested Products",
          badge3_title: tb.badge3_title || "Quick Support",
          badge3_subtitle: tb.badge3_subtitle || "Instant on WhatsApp",
          badge4_title: tb.badge4_title || "Easy Returns",
          badge4_subtitle: tb.badge4_subtitle || "Hassle-Free Exchange",
        });
      }

      // Brand note
      if (storeData.brand_note) {
        const bn = storeData.brand_note;
        setBrandNoteSettings({
          show_banner:
            bn.show_banner !== undefined ? Boolean(bn.show_banner) : true,
          title: bn.title || "🤎 Thank you for supporting our small business!",
          subtitle:
            bn.subtitle || `${storeData.name || "RUDRIX"} | Elegance Meets Care`,
          script_text: bn.script_text || "Self Care Brighter You ♡",
        });
      }

      // Load Categories
      const loadedCats = getStoreCategories(storeData);
      setCategories(loadedCats);
      if (storeData.show_category_section !== undefined) {
        setShowCategorySection(Boolean(storeData.show_category_section));
      } else if (storeData.header?.show_category_section !== undefined) {
        setShowCategorySection(Boolean(storeData.header.show_category_section));
      }

      if (storeData.preferences) {
        setPreferences(storeData.preferences);
      } else {
        setPreferences({
          storeVisibility:
            storeData.visibility !== undefined ? storeData.visibility : true,
          whatsappNotifications:
            storeData.notification !== undefined
              ? storeData.notification
              : true,
          autoReply:
            storeData.auto_reply !== undefined ? storeData.auto_reply : false,
        });
      }
    } catch (error: any) {
      console.error("Error fetching store data:", error);
      if (error.response) {
        setError("Failed to load store data. Please refresh the page.");
      } else if (error.request) {
        setError("Network error. Please check your connection.");
      } else {
        setError("An unexpected error occurred.");
      }
      setOpenSnackbar(true);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchStoreData();
    const unsubscribe = onActiveStoreChange(() => {
      fetchStoreData();
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditClick = () => {
    if (isEditing) {
      // Reset form to original values
      fetchStoreData();
      setErrors({});
      setTouched({});
      setHeroBannerFile(null);
      setRemoveHeroBanner(false);
      setFaviconFile(null);
      setRemoveFavicon(false);
    }
    setIsEditing(!isEditing);
  };

  const handlePreferenceChange = async (name: PreferenceKey) => {
    const newPreferences = { ...preferences, [name]: !preferences[name] };
    setPreferences(newPreferences);

    try {
      if (!storeId) {
        setError("Store ID not found. Please refresh the page.");
        setOpenSnackbar(true);
        setPreferences(preferences);
        return;
      }

      // API call to update preferences
      await Axios.patch(`/store/${storeId}/preferences`, newPreferences);

      setSuccess("Preferences updated successfully!");
      setOpenSnackbar(true);
    } catch (error: any) {
      console.error("Error updating preferences:", error);
      setPreferences(preferences);
      setError("Failed to update preferences. Please try again.");
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError("");
    setSuccess("");
  };

  if (fetchLoading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : success ? "success" : "info"}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Store Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure your Rudrix store
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleEditClick}
          startIcon={
            isEditing ? (
              <Cancel sx={{ color: "#fff" }} />
            ) : (
              <Edit sx={{ color: "#fff" }} />
            )
          }
          sx={{
            borderRadius: 3,
            background: "linear-gradient(135deg, #2196f3, #42a5f5)",
            color: "#fff",
            boxShadow: "0px 4px 6px rgba(0,0,0,0.2)",
            textTransform: "none",
            fontSize: { xs: "0.9rem", sm: "1.05rem" },
            px: { xs: 2, sm: 2.5 },
            py: { xs: 0.6, sm: 0.8 },
          }}
        >
          {isEditing ? "Cancel" : "Edit Settings"}
        </Button>
      </Box>

      {/* Form */}
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          {/* Left Card - Business Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid #e0e0e0",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Business Information
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Basic details about your business
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <StorefrontIcon fontSize="small" /> Business Name *
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter business name"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.businessName && Boolean(errors.businessName)}
                    helperText={touched.businessName && errors.businessName}
                    disabled={!isEditing}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <DescriptionIcon fontSize="small" /> Description *
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    disabled={!isEditing}
                    multiline
                    rows={3}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <WhatsAppIcon fontSize="small" /> WhatsApp Number *
                  </FormLabel>
                  <CountryCodePhoneInput
                    name="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={(e: any) => handleInputChange(e)}
                    onBlur={(e: any) => handleBlur(e)}
                    placeholder="98765 43210"
                    disabled={!isEditing}
                    error={
                      touched.whatsappNumber && Boolean(errors.whatsappNumber)
                    }
                    helperText={
                      touched.whatsappNumber && errors.whatsappNumber
                        ? errors.whatsappNumber
                        : "Include country code (e.g. +91 for India)"
                    }
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <LinkIcon fontSize="small" /> Store URL *
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g., shop or therudrix.in"
                    name="storeUrl"
                    value={formData.storeUrl}
                    onChange={(e) => {
                      const val = e.target.value.toLowerCase().replace(/^(https?:\/\/)/i, "").replace(/\/$/, "");
                      setFormData((prev) => ({ ...prev, storeUrl: val }));
                      if (touched.storeUrl) {
                        const error = validateField("storeUrl", val);
                        setErrors((prev) => ({ ...prev, storeUrl: error || undefined }));
                      }
                    }}
                    onBlur={handleBlur}
                    error={touched.storeUrl && Boolean(errors.storeUrl)}
                    helperText={
                      touched.storeUrl && errors.storeUrl
                        ? errors.storeUrl
                        : formData.storeUrl.includes(".")
                        ? `Custom Domain: https://${formData.storeUrl}`
                        : `Store URL: https://therudrix.in/${formData.storeUrl || "your-store"}`
                    }
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{
                            fontWeight: 600,
                            color: formData.storeUrl.includes(".") ? "primary.main" : "text.secondary",
                            fontSize: "0.85rem",
                          }}
                        >
                          {formData.storeUrl.includes(".") ? "https://" : "therudrix.in/"}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Card - Additional Details */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid #e0e0e0",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Additional Details
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Optional information for customers
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <HomeIcon fontSize="small" /> Business Address
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter your business address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                    disabled={!isEditing}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <AccessTimeIcon fontSize="small" /> Operating Hours
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g., Mon-Fri 9AM-6PM"
                    name="operatingHours"
                    value={formData.operatingHours}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={
                      touched.operatingHours && Boolean(errors.operatingHours)
                    }
                    helperText={touched.operatingHours && errors.operatingHours}
                    disabled={!isEditing}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <LocalShippingIcon fontSize="small" /> Delivery Information
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter delivery details"
                    name="deliveryInfo"
                    value={formData.deliveryInfo}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.deliveryInfo && Boolean(errors.deliveryInfo)}
                    helperText={touched.deliveryInfo && errors.deliveryInfo}
                    disabled={!isEditing}
                    multiline
                    rows={3}
                  />
                </Box>

                <Box>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Image fontSize="small" /> Store Images
                  </FormLabel>

                  {imagePreviews.length > 0 && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {imagePreviews.map((preview, index) => (
                        <Grid item xs={6} sm={4} key={index}>
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              paddingTop: "100%",
                              borderRadius: 2,
                              overflow: "hidden",
                              border: "1px solid #e0e0e0",
                              bgcolor: "#f5f5f5",
                            }}
                          >
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              crossOrigin="anonymous"
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            {isEditing && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveImage(index)}
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  bgcolor: "rgba(255, 255, 255, 0.9)",
                                  "&:hover": {
                                    bgcolor: "rgba(255, 255, 255, 1)",
                                  },
                                }}
                              >
                                <ClearIcon fontSize="small" color="error" />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {isEditing && (
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<Image />}
                      fullWidth
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        borderColor: errors.images ? "#d32f2f" : "#ccc",
                        color: "#555",
                        py: 1.5,
                      }}
                    >
                      Upload Images
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        multiple
                        onChange={handleMultipleImageUpload}
                      />
                    </Button>
                  )}

                  {errors.images && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {errors.images}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Store Profile Header Section Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid #e0e0e0",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Store Profile Header
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure the public storefront header identity area, action buttons, and toggles without changing code
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Sub-section 1: Identity & Branding */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
                >
                  Identity & Branding
                </Typography>

                <Grid container spacing={3}>
                  {/* Store Logo with Preview & Upload */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Store Logo
                    </FormLabel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 2,
                        border: "1px dashed #cbd5e1",
                        borderRadius: 3,
                        bgcolor: "#f8fafc",
                        height: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            overflow: "hidden",
                            mb: 1.5,
                            border: "2px solid #e2e8f0",
                            bgcolor: "#ffffff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          {logoPreview ? (
                            <img
                              src={logoPreview}
                              alt="Store Logo"
                              crossOrigin="anonymous"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <StorefrontIcon sx={{ fontSize: 40, color: "#94a3b8" }} />
                          )}
                        </Box>

                        {isEditing && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              startIcon={<CloudUploadIcon />}
                              sx={{ textTransform: "none", borderRadius: 2 }}
                            >
                              {logoPreview ? "Change Logo" : "Upload Logo"}
                              <input
                                type="file"
                                hidden
                                accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
                                onChange={handleLogoChange}
                              />
                            </Button>
                            {logoPreview && (
                              <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<Delete />}
                                onClick={handleRemoveLogo}
                                sx={{ textTransform: "none" }}
                              >
                                Remove Logo
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, textAlign: "center" }}
                      >
                        Main header logo (PNG, JPG, SVG)
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Store Favicon with Preview & Upload */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Store Favicon
                    </FormLabel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 2,
                        border: "1px dashed #cbd5e1",
                        borderRadius: 3,
                        bgcolor: "#f8fafc",
                        height: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        {/* Realistic Browser Tab Simulation */}
                        <Box
                          sx={{
                            width: "100%",
                            maxWidth: 180,
                            mb: 1.5,
                            p: 1,
                            borderRadius: 2,
                            border: "1px solid #e2e8f0",
                            bgcolor: "#ffffff",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: 1,
                              overflow: "hidden",
                              border: "1px solid #cbd5e1",
                              bgcolor: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {faviconPreview ? (
                              <img
                                src={faviconPreview}
                                alt="Store Favicon"
                                crossOrigin="anonymous"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            ) : logoPreview ? (
                              <img
                                src={logoPreview}
                                alt="Favicon from Logo"
                                crossOrigin="anonymous"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <LanguageIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ fontWeight: 600, fontSize: "0.74rem", color: "#334155" }}
                          >
                            {headerSettings.store_name || formData.businessName || "Store Tab"}
                          </Typography>
                        </Box>

                        {isEditing && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              startIcon={<CloudUploadIcon />}
                              sx={{ textTransform: "none", borderRadius: 2 }}
                            >
                              {faviconPreview ? "Change Favicon" : "Upload Favicon"}
                              <input
                                type="file"
                                hidden
                                accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,image/jpeg,image/webp,.ico"
                                onChange={handleFaviconChange}
                              />
                            </Button>
                            {faviconPreview && (
                              <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<Delete />}
                                onClick={handleRemoveFavicon}
                                sx={{ textTransform: "none" }}
                              >
                                Remove Favicon
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, textAlign: "center" }}
                      >
                        Browser tab icon (.ico, .png, .svg)
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Brand Text Fields */}
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      {/* Store Name */}
                      <Grid item xs={12} sm={6}>
                        <FormLabel
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <StorefrontIcon fontSize="small" /> Store Name
                        </FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., Rudrix Store"
                          name="store_name"
                          value={headerSettings.store_name}
                          onChange={handleHeaderInputChange}
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/* Store Tagline */}
                      <Grid item xs={12} sm={6}>
                        <FormLabel
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <LabelIcon fontSize="small" /> Store Tagline
                        </FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., Premium Quality & Quick Delivery"
                          name="tagline"
                          value={headerSettings.tagline}
                          onChange={handleHeaderInputChange}
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/* Store Phone Number */}
                      <Grid item xs={12} sm={6}>
                        <FormLabel
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <PhoneIcon fontSize="small" /> Store Phone Number
                        </FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., +91 98765 43210"
                          name="phone_number"
                          value={headerSettings.phone_number}
                          onChange={handleHeaderInputChange}
                          disabled={!isEditing}
                          helperText="Used by the storefront Call button"
                        />
                      </Grid>

                      {/* Store Location */}
                      <Grid item xs={12} sm={6}>
                        <FormLabel
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <PlaceIcon fontSize="small" /> Store Location
                        </FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., New Delhi, India"
                          name="location"
                          value={headerSettings.location}
                          onChange={handleHeaderInputChange}
                          disabled={!isEditing}
                          helperText="Shown in header alongside location pin icon"
                        />
                      </Grid>

                      {/* Store Description / Category */}
                      <Grid item xs={12}>
                        <FormLabel
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <DescriptionIcon fontSize="small" /> Description / Category
                        </FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Brief store description or product category"
                          name="description"
                          value={headerSettings.description}
                          onChange={handleHeaderInputChange}
                          disabled={!isEditing}
                          multiline
                          rows={2}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Sub-section 2: Display & Action Toggles */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
                >
                  Display & Action Toggles
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Choose which elements and action buttons appear on the public storefront header
                </Typography>

                <Grid container spacing={2}>
                  {[
                    {
                      label: "Show Logo",
                      key: "show_logo" as const,
                      desc: "Display store logo avatar in header",
                    },
                    {
                      label: "Show Store Name",
                      key: "show_store_name" as const,
                      desc: "Display store name heading",
                    },
                    {
                      label: "Show Description",
                      key: "show_description" as const,
                      desc: "Display category / description text",
                    },
                    {
                      label: "Show Location",
                      key: "show_location" as const,
                      desc: "Display store location pin",
                    },
                    {
                      label: "Show Call Button",
                      key: "show_call_button" as const,
                      desc: "Show circular Call button (initiates phone call)",
                    },
                    {
                      label: "Show Share Button",
                      key: "show_share_button" as const,
                      desc: "Show circular Share button (Web Share API / Copy Link)",
                    },
                    {
                      label: "Show More Button",
                      key: "show_more_button" as const,
                      desc: "Show circular More button (Store profile modal)",
                    },
                  ].map((toggle) => (
                    <Grid item xs={12} sm={6} md={4} key={toggle.key}>
                      <Box
                        sx={{
                          p: 1.5,
                          border: "1px solid #e2e8f0",
                          borderRadius: 2,
                          bgcolor: headerSettings[toggle.key]
                            ? "#f8fafc"
                            : "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#1e293b" }}
                          >
                            {toggle.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {toggle.desc}
                          </Typography>
                        </Box>
                        <Switch
                          checked={headerSettings[toggle.key]}
                          onChange={() => handleHeaderToggleChange(toggle.key)}
                          disabled={!isEditing}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#2196f3",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#2196f3",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Sub-section 3: Customizable Action Button Labels */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
                >
                  Action Button Labels
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Customize the labels shown below the circular action buttons on the public storefront
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormLabel
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <CallIcon fontSize="small" /> Call Button Label
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Call"
                      name="call_button_label"
                      value={headerSettings.call_button_label}
                      onChange={handleHeaderInputChange}
                      disabled={!isEditing}
                      helperText="Default: Call"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormLabel
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <ShareIcon fontSize="small" /> Share Button Label
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Share"
                      name="share_button_label"
                      value={headerSettings.share_button_label}
                      onChange={handleHeaderInputChange}
                      disabled={!isEditing}
                      helperText="Default: Share"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormLabel
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <MoreHorizIcon fontSize="small" /> More Button Label
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="More"
                      name="more_button_label"
                      value={headerSettings.more_button_label}
                      onChange={handleHeaderInputChange}
                      disabled={!isEditing}
                      helperText="Default: More"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Card: Store Categories Management */}
          <Grid item xs={12}>
            <CategoryManager
              categories={categories}
              onChange={(updated) => {
                setCategories(updated);
                saveCategoriesLocally(updated, storeId);
              }}
              showSectionSwitch={true}
              showCategorySection={showCategorySection}
              onShowSectionChange={(show) => setShowCategorySection(show)}
              onSave={handleSaveCategories}
              isSaving={categorySaving}
            />
          </Grid>

          {/* Card 1: Storefront Theme & Appearance */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <PaletteIcon sx={{ color: "#2563eb" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Storefront Theme & Appearance
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Select the visual design system for your public store. Each theme dynamically updates colors, typography, card shapes, and accents.
                </Typography>

                <Grid container spacing={2}>
                  {Object.values(STORE_THEMES).map((th) => {
                    const isSelected = themeSettings.theme_id === th.id;
                    return (
                      <Grid item xs={12} sm={6} md={3} key={th.id}>
                        <Box
                          onClick={() => {
                            if (isEditing) {
                              setThemeSettings((prev) => ({
                                ...prev,
                                theme_id: th.id,
                                accent_color: th.accent,
                              }));
                            }
                          }}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            border: `2px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
                            bgcolor: isSelected ? "#eff6ff" : "#ffffff",
                            cursor: isEditing ? "pointer" : "default",
                            transition: "all 0.2s ease",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            boxShadow: isSelected ? "0 4px 14px rgba(37,99,235,0.15)" : "none",
                            "&:hover": {
                              borderColor: isEditing ? "#2563eb" : undefined,
                            },
                          }}
                        >
                          <Box>
                            {/* Swatches & Active Checkmark */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                              <Box sx={{ display: "flex", gap: 0.75 }}>
                                {th.swatch.map((c, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: "50%",
                                      bgcolor: c,
                                      border: "1px solid rgba(0,0,0,0.15)",
                                    }}
                                  />
                                ))}
                              </Box>
                              {isSelected && (
                                <CheckCircleIcon sx={{ color: "#2563eb", fontSize: 20 }} />
                              )}
                            </Box>

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                              {th.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", lineHeight: 1.3, display: "block" }}>
                              {th.description}
                            </Typography>
                          </Box>

                          <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: isSelected ? "#2563eb" : "#94a3b8" }}>
                              {isSelected ? "Active Theme" : "Click to Select"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Optional Custom Accent Color Override */}
                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 2 }}>
                  <FormLabel sx={{ fontWeight: 600, color: "#334155", fontSize: "0.88rem" }}>
                    Primary Accent Color Override:
                  </FormLabel>
                  <TextField
                    size="small"
                    value={themeSettings.accent_color}
                    onChange={(e) =>
                      setThemeSettings((prev) => ({ ...prev, accent_color: e.target.value }))
                    }
                    disabled={!isEditing}
                    sx={{ width: 140 }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            bgcolor: themeSettings.accent_color,
                            border: "1px solid #ccc",
                            mr: 1,
                          }}
                        />
                      ),
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 2: Top Announcement Bar */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CampaignIcon sx={{ color: "#f59e0b" }} />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Top Announcement Bar
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Enable Announcement Bar
                    </Typography>
                    <Switch
                      checked={announcementSettings.show_announcement}
                      onChange={() =>
                        setAnnouncementSettings((prev) => ({
                          ...prev,
                          show_announcement: !prev.show_announcement,
                        }))
                      }
                      disabled={!isEditing}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Display a slim top notification bar on your store for sales, shipping policies, or store highlights
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Top Tagline
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={announcementSettings.top_tagline}
                      onChange={(e) =>
                        setAnnouncementSettings((prev) => ({
                          ...prev,
                          top_tagline: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., Elegance Meets Care — Premium Essentials"
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Announcement Highlights Text
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={announcementSettings.announcement_text}
                      onChange={(e) =>
                        setAnnouncementSettings((prev) => ({
                          ...prev,
                          announcement_text: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., ✨ Free Delivery • 🛡️ 100% Quality Checked • 🔄 Easy Returns"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Location Badge
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={announcementSettings.location_badge}
                      onChange={(e) =>
                        setAnnouncementSettings((prev) => ({
                          ...prev,
                          location_badge: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., Thane, India"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 3: Hero Section Customization */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <AutoAwesomeIcon sx={{ color: "#c5896e" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Hero Showcase Section
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Configure the primary banner headline, wellness pills, cursive quote, and featured showcase card
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Main Headline
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.headline}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({ ...prev, headline: e.target.value }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., Your Everyday"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Italic Headline Highlight
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.headline_italic}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({
                          ...prev,
                          headline_italic: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., Self-Care Companion"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Badge Pill Text
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.badge_text}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({ ...prev, badge_text: e.target.value }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., Elegance Meets Care"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Cursive Script Accent Quote
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.quote_script}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({
                          ...prev,
                          quote_script: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., Care Looks Good On You ♡"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Feature Badges / Wellness Pills (Comma separated)
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.tags_string}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({
                          ...prev,
                          tags_string: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="e.g., 🪷 Beauty, ♡ Wellness, 🍃 Better You"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Hero Description Subtitle
                    </FormLabel>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={heroSettings.description}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({ ...prev, description: e.target.value }))
                      }
                      disabled={!isEditing}
                      placeholder="Brief inspirational description for the hero banner"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Primary Button Label
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.primary_cta_label}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({
                          ...prev,
                          primary_cta_label: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="Shop Best Sellers"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      WhatsApp Order Button Label
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.whatsapp_cta_label}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({
                          ...prev,
                          whatsapp_cta_label: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="Order via WhatsApp"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Showcase Card Title
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.card_title}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({ ...prev, card_title: e.target.value }))
                      }
                      disabled={!isEditing}
                      placeholder="Beauty & Personal Therapy"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Showcase Card Subtitle
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.card_subtitle}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({
                          ...prev,
                          card_subtitle: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="Featured Collection"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Showcase Delivery Note
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={heroSettings.card_delivery_text}
                      onChange={(e) =>
                        setHeroSettings((prev) => ({
                          ...prev,
                          card_delivery_text: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="Direct Delivery in Thane & All-India"
                    />
                  </Grid>

                  {/* Hero Showcase Card Image */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        mt: 1,
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                          Hero Showcase Card Image
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          Customize the featured photo displayed on the right showcase card in your storefront hero banner.
                          If left unconfigured, your storefront automatically displays your first product's photo.
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 3,
                          flexDirection: { xs: "column", sm: "row" },
                          mb: 2,
                        }}
                      >
                        {/* Image Preview Box */}
                        <Box
                          sx={{
                            width: { xs: "100%", sm: 160 },
                            height: 110,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "2px dashed #cbd5e1",
                            bgcolor: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            flexShrink: 0,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                          }}
                        >
                          {heroBannerPreview ? (
                            <img
                              src={heroBannerPreview}
                              alt="Hero Showcase Preview"
                              crossOrigin="anonymous"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box sx={{ textAlign: "center", p: 1 }}>
                              <Image sx={{ fontSize: 32, color: "#94a3b8" }} />
                              <Typography
                                variant="caption"
                                sx={{ display: "block", color: "#94a3b8", fontSize: "0.72rem", fontWeight: 600 }}
                              >
                                Auto Product Image
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Upload & Action Buttons */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, width: "100%" }}>
                          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              startIcon={<CloudUploadIcon />}
                              disabled={!isEditing}
                              sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                fontWeight: 600,
                              }}
                            >
                              {heroBannerPreview ? "Change Hero Image" : "Upload Hero Image"}
                              <input
                                type="file"
                                hidden
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={handleHeroBannerChange}
                              />
                            </Button>

                            {heroBannerPreview && (
                              <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<Delete />}
                                onClick={handleRemoveHeroBanner}
                                disabled={!isEditing}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                              >
                                Revert to Default / 1st Product
                              </Button>
                            )}
                          </Box>

                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            Recommended 4:3 or landscape image (JPEG, PNG, WebP, max 5MB).
                          </Typography>

                          {/* Direct URL input option */}
                          <Box sx={{ mt: 0.5, maxWidth: 460 }}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Or paste image URL (e.g. https://...)"
                              value={heroSettings.banner_image || ""}
                              onChange={(e) => {
                                const val = e.target.value.trim();
                                setHeroSettings((prev) => ({ ...prev, banner_image: val }));
                                setHeroBannerPreview(val);
                                setHeroBannerFile(null);
                                setRemoveHeroBanner(false);
                              }}
                              disabled={!isEditing}
                              sx={{ bgcolor: "#ffffff" }}
                              InputProps={{
                                sx: { fontSize: "0.82rem" },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                          Dotted Pattern Background Overlay
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          Displays the elegant radial beaded-ring dotted pattern across the hero banner matching the design mockup
                        </Typography>
                      </Box>
                      <Switch
                        checked={heroSettings.show_dotted_pattern}
                        onChange={(e) =>
                          setHeroSettings((prev) => ({
                            ...prev,
                            show_dotted_pattern: e.target.checked,
                          }))
                        }
                        disabled={!isEditing}
                        color="primary"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 4: Trust Badges / Value Proposition */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VerifiedUserIcon sx={{ color: "#10b981" }} />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Trust Badges & Value Proposition
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Enable Trust Badges
                    </Typography>
                    <Switch
                      checked={trustBadgeSettings.show_badges}
                      onChange={() =>
                        setTrustBadgeSettings((prev) => ({
                          ...prev,
                          show_badges: !prev.show_badges,
                        }))
                      }
                      disabled={!isEditing}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  4 customer confidence badges displayed directly beneath the hero section
                </Typography>

                <Grid container spacing={2}>
                  {[
                    { tKey: "badge1_title" as const, sKey: "badge1_subtitle" as const, num: 1 },
                    { tKey: "badge2_title" as const, sKey: "badge2_subtitle" as const, num: 2 },
                    { tKey: "badge3_title" as const, sKey: "badge3_subtitle" as const, num: 3 },
                    { tKey: "badge4_title" as const, sKey: "badge4_subtitle" as const, num: 4 },
                  ].map((b) => (
                    <Grid item xs={12} sm={6} md={3} key={b.num}>
                      <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#f8fafc" }}>
                        <FormLabel sx={{ display: "block", mb: 0.5, fontWeight: 600, fontSize: "0.8rem" }}>
                          Badge {b.num} Title
                        </FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={trustBadgeSettings[b.tKey]}
                          onChange={(e) =>
                            setTrustBadgeSettings((prev) => ({
                              ...prev,
                              [b.tKey]: e.target.value,
                            }))
                          }
                          disabled={!isEditing}
                          sx={{ mb: 1 }}
                        />
                        <FormLabel sx={{ display: "block", mb: 0.5, fontWeight: 500, fontSize: "0.75rem", color: "#64748b" }}>
                          Badge {b.num} Subtitle
                        </FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={trustBadgeSettings[b.sKey]}
                          onChange={(e) =>
                            setTrustBadgeSettings((prev) => ({
                              ...prev,
                              [b.sKey]: e.target.value,
                            }))
                          }
                          disabled={!isEditing}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 5: Brand Appreciation Banner */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FavoriteIcon sx={{ color: "#e11d48" }} />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Brand Appreciation Banner
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Enable Brand Note
                    </Typography>
                    <Switch
                      checked={brandNoteSettings.show_banner}
                      onChange={() =>
                        setBrandNoteSettings((prev) => ({
                          ...prev,
                          show_banner: !prev.show_banner,
                        }))
                      }
                      disabled={!isEditing}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Warm botanical thank-you note banner displayed right above the footer
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Appreciation Title
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={brandNoteSettings.title}
                      onChange={(e) =>
                        setBrandNoteSettings((prev) => ({ ...prev, title: e.target.value }))
                      }
                      disabled={!isEditing}
                      placeholder="🤎 Thank you for supporting our small business!"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Store Subtitle
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={brandNoteSettings.subtitle}
                      onChange={(e) =>
                        setBrandNoteSettings((prev) => ({
                          ...prev,
                          subtitle: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="RUDRIX | Elegance Meets Care"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                      Handwritten Script Accent
                    </FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      value={brandNoteSettings.script_text}
                      onChange={(e) =>
                        setBrandNoteSettings((prev) => ({
                          ...prev,
                          script_text: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      placeholder="Self Care Brighter You ♡"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Save & Cancel Buttons */}
        {isEditing && (
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Save sx={{ color: "#fff" }} />
                )
              }
              sx={{
                borderRadius: 3,
                background: "linear-gradient(135deg, #2196f3, #42a5f5)",
                color: "#fff",
                textTransform: "none",
                fontSize: "1.05rem",
                px: 2.5,
                py: 0.8,
              }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleEditClick}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontSize: "1.05rem",
                px: 2.5,
                py: 0.8,
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      {/* Preferences */}
      <Card sx={{ mt: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            Store Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure store visibility and notifications
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {[
            { label: "Store Visibility", key: "storeVisibility" },
            { label: "WhatsApp Notifications", key: "whatsappNotifications" },
            { label: "Auto-Reply Messages", key: "autoReply" },
          ].map((pref) => (
            <Box
              key={pref.key}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <Typography sx={{ flexGrow: 1 }}>{pref.label}</Typography>
              <Switch
                checked={preferences[pref.key as PreferenceKey]}
                onChange={() =>
                  handlePreferenceChange(pref.key as PreferenceKey)
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#2196f3",
                    "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.08)" },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2196f3",
                  },
                }}
              />
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StoreSettings;
